import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X } from "lucide-react";

export default function ValuationForm({ investment, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    current_valuation: "",
    valuation_date: new Date().toISOString().split("T")[0],
    valuation_notes: "",
  });

  useEffect(() => {
    if (investment) {
      setFormData({
        current_valuation: investment.current_valuation || "",
        valuation_date: investment.valuation_date || new Date().toISOString().split("T")[0],
        valuation_notes: investment.valuation_notes || "",
      });
    }
  }, [investment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const currentValue = parseFloat(formData.current_valuation) || 0;
  const investmentAmount = investment?.investment_amount || 0;
  const unrealizedGain = currentValue - investmentAmount;
  const unrealizedROI = investmentAmount > 0 ? ((unrealizedGain / investmentAmount) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">Oppdater Verdivurdering</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-600 mb-1">Investering</p>
            <p className="font-semibold text-slate-900">{investment?.company_name}</p>
            <p className="text-sm text-slate-500 mt-1">
              Opprinnelig investering: {investment?.investment_amount?.toLocaleString()} kr
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="current_valuation">Nåværende Verdi (NOK) *</Label>
            <Input
              id="current_valuation"
              type="number"
              min="0"
              step="1000"
              value={formData.current_valuation}
              onChange={(e) => setFormData({ ...formData, current_valuation: e.target.value })}
              required
              className="h-11"
              placeholder="f.eks. 5000000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="valuation_date">Verdivurderingsdato *</Label>
            <Input
              id="valuation_date"
              type="date"
              value={formData.valuation_date}
              onChange={(e) => setFormData({ ...formData, valuation_date: e.target.value })}
              required
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="valuation_notes">Notater</Label>
            <Textarea
              id="valuation_notes"
              value={formData.valuation_notes}
              onChange={(e) => setFormData({ ...formData, valuation_notes: e.target.value })}
              rows={3}
              placeholder="Legg til notater om verdivurderingen..."
            />
          </div>

          {formData.current_valuation && (
            <div className={`p-4 rounded-lg border ${unrealizedGain >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              <p className="text-sm font-medium text-slate-700 mb-2">Urealisert resultat:</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Gevinst/Tap:</span>
                <span className={`text-lg font-bold ${unrealizedGain >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {unrealizedGain >= 0 ? "+" : ""}{unrealizedGain.toLocaleString()} kr
                </span>
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-sm text-slate-600">ROI:</span>
                <span className={`text-lg font-bold ${unrealizedROI >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  {unrealizedROI >= 0 ? "+" : ""}{unrealizedROI.toFixed(1)}%
                </span>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : "Oppdater Verdivurdering"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}